import { PermissionAttributesEnum } from "@verbleif/lib/src/composables/Permissions/permissions.enum";
import { ContextVoter } from "./composables/ContextVoter";

/**
 * Mirrors the API price quote security (PriceQuoteAttribute, PriceQuoteVoter and the price quote resources).
 * Every right is a global one; the API has no organisation or location scope for quotes.
 *
 * - GET /price_quotes/config and GET /address_search(/{placeId}): read_config
 * - POST /price_quotes/calculate: calculate
 * - POST /price_quote_share_links (records the quote, a revision included): create_saved
 * - GET /saved_price_quotes: read_saved
 * - GET /saved_price_quotes/{id} and /{id}/pdf: read_saved (PriceQuoteVoter read_saved_item)
 * - DELETE /saved_price_quotes/{id}: delete_saved (PriceQuoteVoter delete_saved_item)
 *
 * update_saved and generate_pdf can be granted but no API operation checks them, so nothing here does either.
 */
export class PriceQuoteVoter extends ContextVoter {
  private hasGlobal(attribute: PermissionAttributesEnum): boolean {
    return this.permissionService.getGlobalPermissionForUser({ attribute }) !== null;
  }

  public canReadConfig(): boolean {
    return this.hasGlobal(PermissionAttributesEnum.GlobalOperationPricequoteReadconfig);
  }

  public canCalculate(): boolean {
    return this.hasGlobal(PermissionAttributesEnum.GlobalOperationPricequoteCalculate);
  }

  public canCreateSaved(): boolean {
    return this.hasGlobal(PermissionAttributesEnum.GlobalOperationPricequoteCreatesaved);
  }

  public canList(): boolean {
    return this.hasGlobal(PermissionAttributesEnum.GlobalOperationPricequoteReadsaved);
  }

  /** One saved quote, its share link and its PDF all come from the item read. */
  public canRead(): boolean {
    return this.hasGlobal(PermissionAttributesEnum.GlobalOperationPricequoteReadsaved);
  }

  public canDelete(): boolean {
    return this.hasGlobal(PermissionAttributesEnum.GlobalOperationPricequoteDeletesaved);
  }

  /**
   * The composer: it loads the packages (config), prices every step (calculate) and ends by recording the quote
   * with its share link (create_saved). Without any one of them the offer cannot be finished.
   */
  public canCompose(): boolean {
    return this.canReadConfig() && this.canCalculate() && this.canCreateSaved();
  }

  /** A revision is a new quote composed from a saved one, which has to be readable to start from. */
  public canRevise(): boolean {
    return this.canRead() && this.canCompose();
  }

  /** The composer's new customer is created through POST /clients (API ClientAttribute::CREATE). */
  public canCreateProspectClient(): boolean {
    return this.canCreateClientContext();
  }

  /** The quote's customer page reads the organisation (API ClientVoter READ). */
  public canOpenClient(client: string): boolean {
    return this.canReadClientContext(client);
  }
}
